import { ReactComponent as WorkText } from "../assets/images/work-fonts/work.svg";
import { ReactComponent as WorkBlank } from "../assets/images/work-fonts/work-blank.svg";
import { useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useCallback, useEffect, useState } from "react";
import FilterButton from "../components/FilterButton";
import { WorkService } from "../services/WorkService";
import { WorkExperiencesType } from "../services/models/WorkServiceModels";
import { JobDescription } from "../components/JobDescription";

/**
 * This component renders the work section of the site.
 * It contains the work title slider effect & work carousel animation
 * @returns the work component
 */
function WorkSections() {
  const control = useAnimation();
  const [ref, inView] = useInView();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [workData, setWorkData] = useState<WorkExperiencesType[]>([]);
  const [filteredWorkData, setFilteredWorkData] = useState<
    WorkExperiencesType[]
  >([]);
  const categories = ["2023", "2022", "2021"];

  useEffect(() => {
    if (inView) {
      control.start("visible");
    } else {
      control.start("hidden");
    }
    // let us also retrieve work data
    WorkService.fetchWorkData().then((data) => {
      setWorkData(data);
      setFilteredWorkData(data);
    });
  }, [control, inView]);

  const setNewSelectedCategories = useCallback(
    (category: string) => {
      setSelectedCategories((prevCategories) =>
        selectedCategories.includes(category)
          ? prevCategories.filter((item) => item !== category)
          : [...prevCategories, category],
      );
      const tempSelectionArray = selectedCategories.includes(category)
        ? selectedCategories.filter((item) => item !== category)
        : [...selectedCategories, category];

      let tempArray: WorkExperiencesType[] = [];
      tempSelectionArray.length !== 0
        ? tempSelectionArray.map((category) => {
            const dateStartedGroup = workData.filter((job) => {
              return job.dateStarted.getFullYear().toString() === category;
            });
            const dateEndedGroup = workData.filter((job) => {
              return job?.dateEnded?.getFullYear().toString() === category;
            });
            tempArray = [...tempArray, ...dateStartedGroup];
            tempArray = [...tempArray, ...dateEndedGroup];
            const uniqueItems = Array.from(new Set(tempArray));

            return setFilteredWorkData(uniqueItems);
          })
        : setFilteredWorkData(workData);
    },
    [selectedCategories, workData],
  );

  return (
    <div
      id="work-container"
      className="w-screen flex flex-col justify-center bg-transparent mt-40 max-md:px-12"
    >
      <div
        id="work-text-container"
        className="bg-transparent h-[80vh] max-lg:ml-0 lg:ml-40 lg:mb-0 mb-10 md:px-28 lg:px-0"
      >
        <WorkText className="bg-transparent w-48 sticky z-10 top-[20vh] max-lg:ml-0 lg:ml-40 " />
        <WorkBlank className="bg-transparent w-48 sticky z-50 top-[20vh] mt-[55vh] max-lg:ml-[.2rem] lg:ml-[10.3rem]" />
      </div>
      <div
        id="work-carousel-containers"
        className="flex flex-row justify-center mt-10"
      >
        <div
          id="web-tablet-work-container"
          className="md:min-h-[100vw] lg:min-h-[70vh] flex w-screen object-center max-md:w-full md:w-10/12 lg:w-8/12 flex-col"
        >
          <div className="max-md:hidden flex flex-row gap-3 mb-5">
            {categories.map((category: string) => {
              return (
                <FilterButton
                  key={category}
                  label={category}
                  setNewSelectedCategories={setNewSelectedCategories}
                  isSelected={selectedCategories.includes(category)}
                />
              );
            })}
          </div>
          <div
            id="job-description-container"
            className="grid grid-cols-1 md:grid-cols-3 gap-y-5 gap-x-3"
          >
            {filteredWorkData?.map((experience) => {
              return (
                <JobDescription
                  company={experience.company}
                  role={experience.role}
                  dateStarted={experience.dateStarted}
                  dateEnded={experience.dateEnded}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkSections;
